import type { LucideIcon } from 'lucide-react';
import { Star, Sparkles, Gamepad2, Palette, Spline, Layers, Smartphone } from 'lucide-react';
import type { Category } from './api';

export interface CategoryDef {
  value: Category | '';
  label: string;
  icon: LucideIcon;
  colorClass: string;
}

export const CATEGORIES: ReadonlyArray<CategoryDef> = [
  { value: '', label: 'Tất cả', icon: Layers, colorClass: 'text-muted-foreground' },
  { value: 'focal', label: 'Tập trung', icon: Star, colorClass: 'text-cat-focal' },
  { value: 'oled', label: 'OLED', icon: Sparkles, colorClass: 'text-cat-oled' },
  { value: 'gaming', label: 'Gaming', icon: Gamepad2, colorClass: 'text-cat-gaming' },
  { value: 'graphic', label: 'Đồ họa', icon: Palette, colorClass: 'text-cat-graphic' },
  { value: 'curved', label: 'Cong', icon: Spline, colorClass: 'text-cat-curved' },
  { value: 'popular', label: 'Phổ thông', icon: Layers, colorClass: 'text-cat-popular' },
  { value: 'portable', label: 'Di động', icon: Smartphone, colorClass: 'text-cat-portable' },
];

export const RESOLUTIONS = ['', 'FHD', '2K', '4K', 'UWQHD'] as const;

export interface SizeBucket {
  value: string;
  label: string;
}

export const SIZE_BUCKETS: ReadonlyArray<SizeBucket> = [
  { value: '', label: 'Mọi kích thước' },
  { value: '24', label: '24"' },
  { value: '25', label: '25"' },
  { value: '27', label: '27"' },
  { value: '32', label: '32"' },
  { value: '34', label: '34" (Ultrawide)' },
  { value: '45', label: '≥ 45"' },
];

// Refresh rate filter dùng range buckets ("min-max" hoặc "min-" open-ended) thay vì
// threshold "≥X" để tránh case bucket đầu tiên ăn gần hết SKUs. Tiered theo use-case
// thực tế của thị trường VN: Văn phòng → Phổ thông → Gaming → Pro/eSports.
export interface RefreshBucket {
  value: string;
  label: string;
}

export const REFRESH_BUCKETS: ReadonlyArray<RefreshBucket> = [
  { value: '',         label: 'Mọi tần số' },
  { value: '60-75',    label: 'Văn phòng (60-75Hz)' },
  { value: '100-144',  label: 'Phổ thông (100-144Hz)' },
  { value: '165-240',  label: 'Gaming (165-240Hz)' },
  { value: '280-',     label: 'Pro / eSports (≥280Hz)' },
];

/** Parse "min-max" or "min-" into { min, max? }. Returns null if value is empty/invalid. */
export function parseRefreshRange(value: string): { min: number; max?: number } | null {
  if (!value) return null;
  const m = value.match(/^(\d+)-(\d*)$/);
  if (!m) return null;
  const min = parseInt(m[1]!, 10);
  const maxStr = m[2];
  const max = maxStr ? parseInt(maxStr, 10) : undefined;
  return Number.isFinite(min) ? { min, max } : null;
}

// Panel types từ catalog — match exact giá trị trong DB.
// Backend filter LOWER() = LOWER() nên case-insensitive nhưng nên giữ nguyên casing chuẩn.
export interface PanelOption {
  value: string;
  label: string;
}

export const PANEL_TYPES: ReadonlyArray<PanelOption> = [
  { value: '', label: 'Mọi tấm nền' },
  { value: 'IPS', label: 'IPS' },
  { value: 'Fast IPS', label: 'Fast IPS' },
  { value: 'Nano IPS', label: 'Nano IPS' },
  { value: 'OLED', label: 'OLED' },
  { value: 'QD-OLED', label: 'QD-OLED' },
  { value: 'WOLED', label: 'WOLED' },
];

export interface SortOption {
  value: string;
  label: string;
}

export const SORT_OPTIONS: ReadonlyArray<SortOption> = [
  { value: 'name:asc', label: 'Tên A→Z' },
  { value: 'refresh_rate:desc', label: 'Hz cao nhất' },
  { value: 'size:desc', label: 'Size lớn nhất' },
  { value: 'size:asc', label: 'Size nhỏ nhất' },
];

export const PAGE_LIMIT = 24;

export function categoryDef(value: string): CategoryDef | undefined {
  return CATEGORIES.find((c) => c.value === value);
}
